import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useBreadcrumbSlot } from '@/shared/hooks/useBreadcrumbSlot';
import { cn } from '@/shared/lib/utils';

interface PageHeaderProps {
  title: string;
  /**
   * Uma frase dizendo o que a pessoa faz nesta tela. Voluntário sem treino.
   *
   * `ReactNode` e não `string` porque na tela de um projeto esta linha carrega
   * os links de contexto (ministério · coordenação) — é o lugar onde a
   * hierarquia da igreja aparece como informação, depois que ela deixou de ser
   * a trilha de navegação.
   */
  description?: ReactNode;
  /**
   * Trilha de navegação, quando a tela está dentro de uma hierarquia. A página
   * continua declarando a trilha aqui, mas ela é desenhada na faixa superior
   * do painel — veja `useBreadcrumbSlot`.
   */
  breadcrumb?: ReactNode;
  /**
   * Nota colada no título, na mesma linha de base.
   *
   * Existe para o estado que descreve o próprio documento em vez de descrever
   * a tela — hoje, só o indicador de autosave do formulário de projeto
   * ("Rascunho salvo automaticamente"). Não é lugar de ação: o que se clica
   * vai em `actions`, e misturar os dois faria o título virar barra de
   * ferramentas.
   */
  titleAside?: ReactNode;
  /** Ações primárias da tela (ex.: "Novo projeto"). */
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  breadcrumb,
  titleAside,
  actions,
  className,
}: PageHeaderProps) {
  const breadcrumbSlot = useBreadcrumbSlot();

  return (
    <div className={cn('mb-6 space-y-2', className)}>
      {breadcrumb && breadcrumbSlot ? createPortal(breadcrumb, breadcrumbSlot) : null}

      {/* Sem moldura por perto (teste isolado, tela fora do AppShell) a trilha
          volta para cima do título — nunca desaparece por falta de destino. */}
      {breadcrumb && !breadcrumbSlot ? (
        <div className="text-sm text-muted-foreground">{breadcrumb}</div>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          {/* `items-baseline` e não `items-center`: a nota é tipografia colada
              ao título, e alinhar pelo centro a faria flutuar visivelmente
              acima da linha em que o título assenta. Quebra para baixo no
              celular (375px) em vez de espremer o nome do projeto. */}
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
            <h1 className="min-w-0 truncate text-2xl font-semibold tracking-tight">{title}</h1>
            {titleAside}
          </div>
          {description ? (
            <div className="text-sm text-muted-foreground">{description}</div>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}
